import { Aspect, MagicResult, Resolution } from '../model';
import type { AiReference } from '../aiApi';

// Provider abstraction for Magic Studio generation. The modal builds a
// provider-agnostic MagicGenerateRequest (prompt + already-resolved base64
// reference images) and hands it to whatever MagicProvider the registry
// selects — direct Gemini (key in browser), our backend proxy, or the demo
// fake. Adding a provider or swapping the local client for the backend never
// touches the modal.

// A reference image already resolved to inline base64 bytes, ready for any
// provider. The app layer (imageResolver) is responsible for turning the
// session's MagicRef[] into these — providers never fetch URLs themselves.
export type MagicImage = {
  // e.g. "image/png" — usable as a Gemini mime_type.
  mimeType: string;
  // Raw base64 (no "data:<mime>;base64," prefix).
  dataBase64: string;
  // Display name / filename, best effort.
  name?: string;
  // Source Asset Lab id when the image came from an asset (vs an upload/paste).
  assetId?: number | null;
  // Decoded byte size, used to decide inline base64 vs Files API upload.
  bytes?: number;
};

export type MagicGenerateRequest = {
  // The model to run: a studio id (MODELS in model.ts) on the direct path, the
  // technical model name ('gpt-image-1', 'gemini-2.5-flash-image') on the
  // backend path — the modal fills in whichever the selected provider expects.
  modelId: string;
  prompt: string;
  aspect: Aspect;
  // Target output resolution (Gemini imageConfig.imageSize) — honoured on both
  // paths: the direct client puts it in imageConfig, backendProvider sends it as
  // `resolution`. Each drops it for a model that doesn't offer the value, and
  // OpenAI models take a canvas `size` from the aspect instead.
  resolution?: Resolution;
  // DIRECT path references: already base64-resolved and deduped by the app layer
  // (imageResolver.resolveRefs). Empty on the backend path.
  images: MagicImage[];
  // BACKEND path references: `{ asset_id }` for assets the proxy can fetch
  // itself, `{ image_base64 }` for everything else
  // (imageResolver.resolveBackendRefs). Absent on the direct path.
  references?: AiReference[];
  // Optional cancellation for in-flight requests.
  signal?: AbortSignal;
};

// Throwable error carrying a user-facing message for the modal's toast.
// Providers wrap transport/API failures in this; anything else is treated as
// an unexpected error and shown with a generic message.
export class MagicProviderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MagicProviderError';
  }
}

export interface MagicProvider {
  // Stable id for logging / debugging ("gemini" | "backend" | "fake").
  readonly id: string;
  // Whether this provider can serve the given studio model id.
  supportsModel(modelId: string): boolean;
  generate(req: MagicGenerateRequest): Promise<MagicResult>;
}
